import { CircularProgress, useTheme, Box } from "@mui/material";
import type React from "react";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { tokens } from "../../../theme";
import { UploadedFiles, VenueFormData } from "../../../types/venue";
import { StepItem } from "../../../types/steps";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../components/common/ShowToast";
import apiService from "../../../service/Api";
import { logout } from "../../../store/slice/AuthSlice";
import { PERMISSIONS } from "../../../constants/permissions";
import { checkPermission } from "../../../service/permissionGuard";
import VenueEssentials from "./steps/VenueEssentials";
import Power from "./steps/Power";
import Internet from "./steps/InternetDetails";
import PASystems from "./steps/PAsystems";
import Rooms from "./steps/Rooms";
import Contacts from "./steps/Contacts";
import AddVenueLayout from "./AddVenueLayout";

// ── Interfaces ────────────────────────────────────────────────────────────────
interface FileUrls {
  venue: string[];
  paSystem: string[];
  equipment: string[];
  floorPlan: string[];
}

interface VenueDocument {
  id: number;
  file: string;
  file_type: string;
  venue: number;
}

interface ApiChild {
  id: number;
  name: string;
  venue_type: string;
}

interface ApiRoomResponse {
  id: number;
  name: string;
  capacity: string;
  venue_type: string;
  room_type?: string;
  layouts: {
    id: number;
    layoutType: string;
    capacity: string;
  }[];
  internet_service_provider?: string | null;
  wifi_name?: string | null;
  wifi_password?: string | null;
  internet_upload_speed?: string | null;
  router_accessibility?: string | null;
  router_distance_from_livestream?: number | string | null;
  files?: { id: number; file: string; file_type: string; venue: number }[];
}

interface ApiVenueResponse {
  id: number;
  name: string;
  notes: string;
  completeness_score?: number;
  venue_type: string;
  address_one: string;
  address_two: string;
  suburb: string;
  city: string;
  latitude?: number | string | null;
  longitude?: number | string | null;
  capacity: string;
  children?: ApiChild[];
  layouts: {
    id: number;
    layout_type: string;
    capacity: string;
    created_at: string;
    updated_at: string;
    venue: number;
  }[];
  contacts: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    twitter_handle: string | null;
    instagram_handle: string | null;
    facebook_handle: string | null;
    created_at: string;
    updated_at: string;
    venue: number;
  }[];
  files: { id: number; file: string; file_type: string; venue: number }[];
  has_power: boolean;
  power_type: string;
  power_outage_rate: string;
  power_backup: string;
  backup_power_capacity: string;
  power_distance_from_livestream_desk: string | null;
  internet_service_provider: string | null;
  wifi_name: string;
  wifi_password: string;
  internet_upload_speed: string;
  router_accessibility: string;
  router_distance_from_livestream: string | null;

  website: string | null;
  facebook: string | null;
  instagram: string | null;
  has_pa_system: boolean;
  power_socket_type: string;
  pa_system_distance_from_livestream: string;
  pa_system_provider: string;
  other_pa_system_service_provider: string;
  pa_system_contact_name: string;
  pa_system_contact_phone: string;
  pa_system_contact_email: string;
  created_at: string;
  updated_at: string;
}

// Mirrors DEFAULT_LAYOUTS in Rooms.tsx so all layout slots are present
const DEFAULT_LAYOUTS = [
  { id: "theater", name: "Theater", capacity: 0 },
  { id: "banquet", name: "Banquet", capacity: 0 },
  { id: "cocktail", name: "Cocktail", capacity: 0 },
  { id: "classroom", name: "Classroom", capacity: 0 },
  { id: "boardroom", name: "Boardroom", capacity: 0 },
  { id: "u_shape", name: "U-Shape", capacity: 0 },
  { id: "cabaret", name: "Cabaret", capacity: 0 },
  { id: "other", name: "Other", capacity: 0 },
];

// A room id is "existing" if it is a plain numeric string (set from API id)
const isExistingRoom = (id: string) =>
  !id.startsWith("draft-") && !id.startsWith("room-") && !isNaN(Number(id));

const EditVenue: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { id } = useParams<{ id: string }>();
  const [editingContactId, setEditingContactId] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modifiedRoomIds, setModifiedRoomIds] = useState<Set<string>>(
    new Set(),
  );

  // Original URLs loaded from the API
  const [fileUrls, setFileUrls] = useState<FileUrls>({
    venue: [],
    paSystem: [],
    equipment: [],
    floorPlan: [],
  });

  // URLs the user has NOT removed — drives delete-on-submit logic
  const [remainingFileUrls, setRemainingFileUrls] = useState<FileUrls>({
    venue: [],
    paSystem: [],
    equipment: [],
    floorPlan: [],
  });

  const [venueDocuments, setVenueDocuments] = useState<VenueDocument[]>([]);

  // Track original room IDs so we can DELETE removed ones on submit
  const [originalRoomIds, setOriginalRoomIds] = useState<string[]>([]);
  const [existingRoomPhotos, setExistingRoomPhotos] = useState<
    Record<string, string[]>
  >({});
  const [formData, setFormData] = useState<VenueFormData>({
    venueName: "",
    venueType: "",
    addressOne: "",
    addressTwo: "",
    suburb: "",
    city: "",
    capacity: "",
    rooms: [],
    layouts: [{ layoutType: "", capacity: "" }],
    contact_id: undefined,
    contacts_ids: [],
    contactPersonName: "",
    contactPersonSurname: "",
    contactPersonEmail: "",
    contactPersonPhone: "",
    doesVenueHavePower: "",
    notes: "",

    website: "",
    facebook: "",
    instagram: "",
    powerType: "",
    powerOutageRate: "",
    powerBackup: "",
    backupPowerCapacity: "",
    router_distance_from_livestream: "",
    powerSocketType: "",
    internet_service_provider: "",
    wifi_name: "",
    wifi_password: "",
    internet_upload_speed: "",
    router_accessibility: "",
    has_pa_system: "",
    pa_system_provider: "",
    pa_system_distance_from_livestream: "",
    pa_system_contact_name: "",
    other_pa_system_service_provider: "",
    pa_system_contact_phone: "",
    pa_system_contact_email: "",
    power_distance_from_livestream_desk: "",
    uploadedFiles: { venue: [], paSystem: [], equipment: [], floorPlan: [] },
  });

  const [uploadedFiles] = useState<UploadedFiles>({
    venue: [],
    paSystem: [],
    equipment: [],
    floorPlan: [],
  });

  const steps: StepItem[] = [
    { label: "Venue Essentials" },
    { label: "Power" },
    { label: "Internet Details" },
    { label: "PA Systems" },
    { label: "Rooms" },
    { label: "Contacts" },
  ];

  // ── Map a single room API response → RoomData ──────────────────────────────
  const mapRoom = (room: ApiRoomResponse) => {
    const mergedLayouts = DEFAULT_LAYOUTS.map((def) => {
      const saved = room.layouts?.find((l) => l.layoutType === def.id);
      return saved
        ? { id: def.id, name: def.name, capacity: Number(saved.capacity) }
        : { ...def };
    });

    return {
      id: String(room.id),
      name: room.name ?? "",
      capacity: room.capacity ?? "",
      room_type: room.room_type ?? room.venue_type ?? "",
      layouts: mergedLayouts,
      photos: [] as File[],
      floorplans: [] as File[],
      internet_service_provider: room.internet_service_provider ?? "",
      wifi_name: room.wifi_name ?? "",
      wifi_password: room.wifi_password ?? "",
      internet_upload_speed: room.internet_upload_speed ?? "",
      router_accessibility: (room.router_accessibility === "no"
        ? "no"
        : "yes") as "yes" | "no",
      router_distance_from_livestream:
        room.router_distance_from_livestream?.toString() ?? "",
      // carry existing photo URLs on the mapped object temporarily
      _existingPhotos: (room.files ?? [])
        .filter((f) => f.file_type === "venue_photos")
        .map((f) => f.file),
    };
  };

  // ── Map API response → VenueFormData ──────────────────────────────────────
  const mapApiResponseToFormData = (
    apiData: ApiVenueResponse,
    rooms: ApiRoomResponse[],
  ): VenueFormData => {
    const contact = apiData.contacts[0] || null;
    if (!contact) console.warn(`No contacts found for venue ID ${apiData.id}.`);

    const venuePhotoUrls = apiData.files
      .filter((f) => f.file_type === "venue_photos")
      .map((f) => f.file);
    const paSystemUrls = apiData.files
      .filter((f) => f.file_type === "pa_system")
      .map((f) => f.file);
    const equipmentUrls = apiData.files
      .filter((f) => f.file_type === "equipment")
      .map((f) => f.file);
    const floorPlanUrls = apiData.files
      .filter((f) => f.file_type === "floor_plan")
      .map((f) => f.file);

    setFileUrls({
      venue: venuePhotoUrls,
      paSystem: paSystemUrls,
      equipment: equipmentUrls,
      floorPlan: floorPlanUrls,
    });
    setRemainingFileUrls({
      venue: venuePhotoUrls,
      paSystem: paSystemUrls,
      equipment: equipmentUrls,
      floorPlan: floorPlanUrls,
    });
    setVenueDocuments(apiData.files);

    const mappedRooms = rooms.map(mapRoom);
    const roomPhotosMap: Record<string, string[]> = {};
    mappedRooms.forEach((r) => {
      if (r._existingPhotos.length > 0) {
        roomPhotosMap[r.id] = r._existingPhotos;
      }
    });
    setExistingRoomPhotos(roomPhotosMap);

    // ── Record original IDs for delete tracking ──
    setOriginalRoomIds(mappedRooms.map((r) => r.id));

    return {
      venueName: apiData.name?.trim() ?? "",
      venueType: apiData.venue_type?.trim() ?? "",
      addressOne: apiData.address_one?.trim() ?? "",
      addressTwo: apiData.address_two?.trim() ?? "",
      suburb: apiData.suburb?.trim() ?? "",
      completeness_score: apiData.completeness_score,
      city: apiData.city?.trim() ?? "",
      latitude:
        apiData.latitude !== null && apiData.latitude !== undefined
          ? Number(apiData.latitude)
          : null,
      longitude:
        apiData.longitude !== null && apiData.longitude !== undefined
          ? Number(apiData.longitude)
          : null,
      notes: apiData.notes?.trim() ?? "",
      website: apiData.website?.trim() ?? "",
      facebook: apiData.facebook?.trim() ?? "",
      instagram: apiData.instagram?.trim() ?? "",
      capacity: apiData.capacity?.trim() ?? "",
      layouts: apiData.layouts?.map((layout) => ({
        layoutType: layout.layout_type?.trim() ?? "",
        capacity: layout.capacity?.trim() ?? "",
      })) ?? [{ layoutType: "", capacity: "" }],
      contact_id: contact?.id ?? undefined,
      contacts_ids: apiData.contacts.map((c) => c.id),
      contactPersonName: contact?.first_name?.trim() ?? "",
      contactPersonSurname: contact?.last_name?.trim() ?? "",
      contactPersonEmail: contact?.email?.trim() ?? "",
      contactPersonPhone: contact?.phone?.trim() ?? "",
      doesVenueHavePower: apiData.has_power ? "yes" : "no",
      powerType: apiData.power_type?.trim() ?? "",
      powerOutageRate: apiData.power_outage_rate?.trim() ?? "",
      powerBackup: apiData.power_backup?.trim() ?? "",
      backupPowerCapacity: apiData.backup_power_capacity?.trim() ?? "",
      router_distance_from_livestream:
        apiData.router_distance_from_livestream?.trim() ?? "",
      powerSocketType: apiData.power_socket_type?.trim() ?? "",
      internet_service_provider:
        apiData.internet_service_provider?.trim() ?? "",
      wifi_name: apiData.wifi_name?.trim() ?? "",
      wifi_password: apiData.wifi_password?.trim() ?? "",
      internet_upload_speed: apiData.internet_upload_speed ?? "",
      router_accessibility: apiData.router_accessibility?.trim() ?? "",
      has_pa_system: apiData.has_pa_system ? "yes" : "no",
      pa_system_provider: apiData.pa_system_provider?.trim() ?? "",
      pa_system_distance_from_livestream:
        apiData.pa_system_distance_from_livestream?.trim() ?? "",
      pa_system_contact_name: apiData.pa_system_contact_name?.trim() ?? "",
      other_pa_system_service_provider:
        apiData.other_pa_system_service_provider?.trim() ?? "",
      pa_system_contact_phone: apiData.pa_system_contact_phone?.trim() ?? "",
      pa_system_contact_email: apiData.pa_system_contact_email?.trim() ?? "",
      power_distance_from_livestream_desk:
        apiData.power_distance_from_livestream_desk?.trim() ?? "",

      contacts: apiData.contacts.map((c) => ({
        id: String(c.id),
        name: c.first_name?.trim() ?? "",
        surname: c.last_name?.trim() ?? "",
        role: "",
        email: c.email?.trim() ?? "",
        phone: c.phone?.trim() ?? "",
      })),
      rooms: mappedRooms,
      uploadedFiles: { venue: [], paSystem: [], equipment: [], floorPlan: [] },
    };
  };

  // ── Fetch venue then each child room individually (mirrors useVenueData) ───
  useEffect(() => {
    const fetchVenueData = async () => {
      if (!id) {
        showErrorToast("No venue ID provided");
        return;
      }
      setIsSubmitting(true);
      try {
        const response = await apiService.get<ApiVenueResponse>(
          `/api/venues/venue/${id}/`,
        );
        const apiData = response.data;

        const rooms: ApiRoomResponse[] =
          (apiData.children as unknown as ApiRoomResponse[]) ?? [];

        setFormData(mapApiResponseToFormData(apiData, rooms));
      } catch (err) {
        console.error("Failed to fetch venue data:", err);
        let errorMessage = "Failed to load venue data. Please try again later.";
        if (
          (err as { response?: { status?: number } })?.response?.status === 401
        ) {
          errorMessage = "Unauthorized. Please log in again.";
          dispatch(logout());
          navigate("/login");
        }
        showErrorToast(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    };
    fetchVenueData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleInputChange = (
    fieldOrEvent:
      | keyof VenueFormData
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | { target: { name: string; value: string } },
    value?: string,
  ) => {
    if (typeof fieldOrEvent === "string") {
      setFormData((prev) => ({ ...prev, [fieldOrEvent]: value }));
    } else {
      const { name, value: val } = fieldOrEvent.target;
      setFormData((prev) => ({ ...prev, [name]: val }));
    }
  };

  const handleFilesChange =
    (category: keyof VenueFormData["uploadedFiles"]) =>
    async (files: File[]) => {
      const validFiles = files.filter(
        (file) => file instanceof File && file.size > 0 && file.name,
      );
      setFormData((prev) => ({
        ...prev,
        uploadedFiles: {
          ...prev.uploadedFiles,
          [category]: [...(prev.uploadedFiles[category] ?? []), ...validFiles],
        },
      }));
    };

  const handleRemoveExistingFile = (category: keyof FileUrls, url: string) => {
    setRemainingFileUrls((prev) => ({
      ...prev,
      [category]: prev[category].filter((u) => u !== url),
    }));
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) setActiveStep((p) => p + 1);
  };
  const handlePrevious = () => {
    if (activeStep > 0) setActiveStep((p) => p - 1);
  };

  // ── Submit (PUT venue + sync rooms like AddVenue does) ────────────────────
  const handleSubmit = async () => {
    checkPermission(PERMISSIONS.VENUE_CHANGE);
    if (!id) {
      showErrorToast("No venue ID provided");
      return;
    }
    setIsSubmitting(true);
    try {
      // ── 1. Delete venue-level photos the user removed ────────────────────
      const categoryMap: Record<keyof FileUrls, string> = {
        venue: "venue_photos",
        paSystem: "pa_system",
        equipment: "equipment",
        floorPlan: "floor_plan",
      };

      const deletePhotoPromises: Promise<unknown>[] = [];
      for (const cat in categoryMap) {
        const category = cat as keyof FileUrls;
        const backendType = categoryMap[category];
        const deletedUrls = fileUrls[category].filter(
          (url) => !remainingFileUrls[category].includes(url),
        );
        for (const delUrl of deletedUrls) {
          const doc = venueDocuments.find(
            (d) => d.file === delUrl && d.file_type === backendType,
          );
          if (doc)
            deletePhotoPromises.push(
              apiService.delete(`/api/venues/document/${doc.id}/`),
            );
        }
      }
      await Promise.all(deletePhotoPromises);

      // ── 2. Delete rooms the user removed ────────────────────────────────
      const currentRoomIds = new Set((formData.rooms ?? []).map((r) => r.id));
      const deletedRoomIds = originalRoomIds.filter(
        (rid) => !currentRoomIds.has(rid),
      );
      await Promise.all(
        deletedRoomIds.map((rid) =>
          apiService.delete(`/api/venues/venue/${rid}/`),
        ),
      );

      // ── 3. PUT the main venue (rooms excluded — handled separately) ──────
      const formDataToSend = new FormData();
      const validLayouts = formData.layouts.filter(
        (l) => l.layoutType.trim() !== "",
      );

      const payload = {
        name: formData.venueName,
        venue_type: formData.venueType,
        address_one: formData.addressOne,
        address_two: formData.addressTwo,
        suburb: formData.suburb,
        city: formData.city,
        latitude: formData.latitude ?? null,
        longitude: formData.longitude ?? null,
        capacity: formData.capacity,
        layouts: validLayouts,
        contact_id: formData.contact_id,
        contacts_ids: [
          ...(Array.isArray(formData.contacts_ids)
            ? formData.contacts_ids
            : []),
          ...(formData.contact_id ? [Number(formData.contact_id)] : []),
        ],
        contact_person_name: formData.contactPersonName,
        contact_person_surname: formData.contactPersonSurname,
        contact_person_email: formData.contactPersonEmail,
        contact_person_phone: formData.contactPersonPhone,
        notes: formData.notes,
        website: formData.website || "",
        facebook: formData.facebook || "",
        instagram: formData.instagram || "",
        has_power: formData.doesVenueHavePower === "yes",
        power_type: formData.powerType || " ",
        power_outage_rate: formData.powerOutageRate || " ",
        power_backup: formData.powerBackup || " ",
        backup_power_capacity: formData.backupPowerCapacity || " ",
        router_distance_from_livestream:
          formData.router_distance_from_livestream
            ? parseInt(formData.router_distance_from_livestream)
            : null,
        power_socket_type: formData.powerSocketType || " ",
        internet_service_provider: formData.internet_service_provider,
        wifi_name: formData.wifi_name,
        wifi_password: formData.wifi_password,
        internet_upload_speed: formData.internet_upload_speed,
        router_accessibility: formData.router_accessibility,
        has_pa_system: formData.has_pa_system === "yes",
        pa_system_provider: formData.pa_system_provider,
        pa_system_distance_from_livestream:
          formData.pa_system_distance_from_livestream
            ? parseInt(formData.pa_system_distance_from_livestream)
            : null,
        pa_system_contact_name: formData.pa_system_contact_name,
        other_pa_system_service_provider:
          formData.other_pa_system_service_provider,
        pa_system_contact_phone: formData.pa_system_contact_phone,
        pa_system_contact_email: formData.pa_system_contact_email,
        power_distance_from_livestream_desk:
          formData.power_distance_from_livestream_desk
            ? parseInt(formData.power_distance_from_livestream_desk)
            : " ",
        show_rooms: (formData.rooms ?? []).length > 0,
        rooms: [], // handled separately below
      };

      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );

      formDataToSend.append("data", JSON.stringify(filteredPayload));

      // Venue-level new file uploads
      formData.uploadedFiles.venue.forEach((file, index) => {
        if (
          file instanceof File &&
          file.size > 0 &&
          file.type.startsWith("image/")
        )
          formDataToSend.append(`venue_photos[${index}]`, file);
      });
      formData.uploadedFiles.paSystem.forEach((file, index) => {
        if (
          file instanceof File &&
          file.size > 0 &&
          file.type.startsWith("image/")
        )
          formDataToSend.append(`pa_system_photos[${index}]`, file);
      });
      formData.uploadedFiles.equipment.forEach((file, index) => {
        if (
          file instanceof File &&
          file.size > 0 &&
          file.type.startsWith("image/")
        )
          formDataToSend.append(`equipment_photos[${index}]`, file);
      });
      formData.uploadedFiles.floorPlan.forEach((file, index) => {
        if (
          file instanceof File &&
          file.size > 0 &&
          (file.type.startsWith("image/") || file.type === "application/pdf")
        )
          formDataToSend.append(`floor_plan_photos[${index}]`, file);
      });

      await apiService.put(
        `/api/venues/venue/${id}/full-update/`,
        formDataToSend,
        false,
      );

      // ── 4. Sync rooms — same pattern as AddVenue ─────────────────────────
      const rooms = formData.rooms ?? [];

      if (rooms.length > 0) {
        await Promise.all(
          rooms.map(async (room) => {
            const existing = isExistingRoom(room.id);

            if (existing && !modifiedRoomIds.has(room.id)) return;
            const roomPayload = {
              name: room.name,
              address_one: formData.addressOne,
              capacity: room.capacity,

              parent_id: Number(id),
              country: "ZW",
              internet_service_provider: room.internet_service_provider,
              wifi_name: room.wifi_name,
              wifi_password: room.wifi_password,
              internet_upload_speed: room.internet_upload_speed,
              router_accessibility: room.router_accessibility,
              router_distance_from_livestream:
                room.router_distance_from_livestream
                  ? parseInt(room.router_distance_from_livestream)
                  : null,
              // ── SAFE LAYOUT MAPPING ──────────────────────────────────────────
              layouts: (room.layouts ?? [])
                .filter((l) => {
                  const parsedCap = parseInt(String(l.capacity), 10);
                  return !isNaN(parsedCap) && parsedCap > 0; // Keep layouts with valid capacity
                })
                .map((l) => ({
                  layoutType: l.id, // e.g., "theater", "banquet"
                  capacity: String(l.capacity),
                })),
            };

            const hasFiles =
              room.photos.some((f) => f instanceof File && f.size > 0) ||
              room.floorplans.some((f) => f instanceof File && f.size > 0);

            // Always use FormData — backend requires data wrapped as JSON string
            const buildFormData = (files?: {
              photos: File[];
              floorplans: File[];
            }) => {
              const fd = new FormData();
              fd.append("data", JSON.stringify(roomPayload));
              if (files) {
                files.photos.forEach((file, i) => {
                  if (file instanceof File && file.size > 0)
                    fd.append(`venue_photos[${i}]`, file);
                });
                files.floorplans.forEach((file, i) => {
                  if (file instanceof File && file.size > 0)
                    fd.append(`floor_plan_photos[${i}]`, file);
                });
              }
              return fd;
            };

            if (isExistingRoom(room.id)) {
              return apiService.put(
                `/api/venues/venue/${room.id}/full-update/`,
                buildFormData(hasFiles ? room : undefined),
                false,
              );
            } else {
              return apiService.post(
                "/api/venues/venue/full-create/",
                buildFormData(hasFiles ? room : undefined),
                false,
              );
            }
          }),
        );
      }

      // BEFORE
      showSuccessToast(
        rooms.length > 0
          ? `Venue and ${rooms.length} room(s) updated successfully!`
          : "Venue updated successfully!",
      );
      setModifiedRoomIds(new Set());
      setTimeout(() => navigate(`/venues/view/${id}`), 2000);
    } catch (error) {
      const errorResponse = (
        error as {
          response?: {
            status?: number;
            data?: { detail?: string; errors?: Record<string, unknown> };
          };
        }
      )?.response;
      let errorMessage = "Failed to update venue. Please try again.";
      if (errorResponse?.status === 401) {
        errorMessage = "Unauthorized: Please log in again.";
        dispatch(logout());
        navigate("/login");
      } else if (errorResponse?.data?.detail) {
        errorMessage = errorResponse.data.detail;
        if (errorResponse.data.errors) {
          errorMessage +=
            ": " +
            Object.entries(errorResponse.data.errors)
              .map(([field, errors]) => {
                if (Array.isArray(errors))
                  return `${field}: ${errors.join(", ")}`;
                if (typeof errors === "object" && errors !== null)
                  return `${field}: ${Object.entries(errors)
                    .map(
                      ([i, m]) =>
                        `File ${i}: ${Array.isArray(m) ? m.join(", ") : m}`,
                    )
                    .join("; ")}`;
                return `${field}: ${JSON.stringify(errors)}`;
              })
              .join("; ");
        }
      } else if (errorResponse?.data) {
        errorMessage = JSON.stringify(errorResponse.data);
      }
      showErrorToast(errorMessage);
      console.error("Error updating venue:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Step rendering ─────────────────────────────────────────────────────────
  const renderStepContent = () => {
    if (isSubmitting && activeStep === 0) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <CircularProgress sx={{ color: colors.primary[400] }} />
        </Box>
      );
    }

    switch (activeStep) {
      case 0:
        return (
          <VenueEssentials
            formData={formData}
            handleInputChange={handleInputChange}
            setFormData={setFormData}
            uploadedFiles={uploadedFiles}
            handleFilesChange={handleFilesChange}
          />
        );
      case 1:
        return (
          <Power
            formData={formData}
            handleInputChange={handleInputChange}
            setFormData={setFormData}
          />
        );
      case 2:
        return (
          <Internet
            formData={formData}
            handleInputChange={handleInputChange}
            setFormData={setFormData}
          />
        );
      case 3:
        return (
          <PASystems
            formData={formData}
            handleInputChange={handleInputChange}
            setFormData={setFormData}
          />
        );
      case 4:
        return (
          <Rooms
            rooms={formData.rooms ?? []}
            onRoomsChange={(updatedRooms) => {
              // Mark any existing room that actually changed
              setModifiedRoomIds((prev) => {
                const next = new Set(prev);
                updatedRooms.forEach((updatedRoom) => {
                  if (!isExistingRoom(updatedRoom.id)) return; // new rooms always POST

                  const original = formData.rooms?.find(
                    (r) => r.id === updatedRoom.id,
                  );
                  if (!original) return;

                  // Fallbacks ensure stringify runs safely even if updatedRooms drops layouts temporarily
                  const currentLayouts =
                    updatedRoom.layouts || original.layouts || [];
                  const originalLayouts = original.layouts || [];

                  const changed =
                    updatedRoom.name !== original.name ||
                    updatedRoom.capacity !== original.capacity ||
                    updatedRoom.room_type !== original.room_type ||
                    updatedRoom.internet_service_provider !==
                      original.internet_service_provider ||
                    updatedRoom.wifi_name !== original.wifi_name ||
                    updatedRoom.wifi_password !== original.wifi_password ||
                    updatedRoom.internet_upload_speed !==
                      original.internet_upload_speed ||
                    updatedRoom.router_accessibility !==
                      original.router_accessibility ||
                    updatedRoom.router_distance_from_livestream !==
                      original.router_distance_from_livestream ||
                    JSON.stringify(currentLayouts) !==
                      JSON.stringify(originalLayouts) ||
                    updatedRoom.photos.length !== original.photos.length ||
                    updatedRoom.floorplans.length !==
                      original.floorplans.length;

                  if (changed) next.add(updatedRoom.id);
                });
                return next;
              });

              // Save the new values, but explicitly look back at previous state
              // to fill in layouts if the sub-component didn't return them
              setFormData((prev) => ({
                ...prev,
                rooms: updatedRooms.map((room) => {
                  const previousStateMatch = prev.rooms?.find(
                    (r) => r.id === room.id,
                  );
                  return {
                    ...room,
                    layouts: room.layouts || previousStateMatch?.layouts || [],
                  };
                }),
              }));
            }}
          />
        );
      case 5:
        return (
          <Contacts
            formData={formData}
            handleInputChange={handleInputChange}
            setFormData={setFormData}
            editingContactId={editingContactId}
            setEditingContactId={setEditingContactId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <AddVenueLayout
        steps={steps.map((s) => s.label)}
        activeStep={activeStep}
        formData={formData}
        onNext={handleNext}
        onPrevious={handlePrevious}
        onSubmit={handleSubmit}
        onStepClick={setActiveStep}
        isSubmitting={isSubmitting}
        setFormData={setFormData}
        onStepChange={setActiveStep}
        editingContactId={editingContactId}
        setEditingContactId={setEditingContactId}
        onRoomsChange={(rooms) => setFormData((prev) => ({ ...prev, rooms }))}
        existingFileUrls={remainingFileUrls}
        onRemoveExistingFile={handleRemoveExistingFile}
        existingRoomPhotos={existingRoomPhotos}
        onRemoveExistingRoomPhoto={(roomId, url) =>
          setExistingRoomPhotos((prev) => ({
            ...prev,
            [roomId]: prev[roomId].filter((u) => u !== url),
          }))
        }
      >
        {renderStepContent()}
      </AddVenueLayout>
    </>
  );
};

export default EditVenue;
